import { useEffect, useRef, useState } from "react";
import { usePhaseAnimation } from "shared/context/PhaseAnimationContext";
import { ANIMATION_MAX_MS } from "shared/context/PhaseAnimationContext";
import { ProjectNextStatusService } from "shared/services/project";
import { handleErrors } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";
import { Icons } from "assets";
import classNames from "classnames";
import CustomCheckbox from "shared/components/common/customCheckbox";
import CustomModal from "shared/components/common/customModal";
import CustomPageHeading from "shared/components/common/customPageHeading";
import styles from "./style.module.scss";
import CustomBadge from "shared/components/common/customBadge";

interface ProceedPhaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestRevision?: () => void;
  title?: string;
  description?: string;
  data?: any;
  updateData?: (data: any) => void;
  icon?: any;
  iconStyle?: any;
  iconContainerStyle?: any;
  buttonText?: string;
  lists?: any;
  setLists?: (val: any[]) => void;
  forceReload?: boolean;
  projectId?: number;
  revisions?: any[];
  onSuccess?: (options?: { nextPhaseLabel: string }) => void;
  currentStatus?: string;
}
interface ImageItem {
  id: string;
  imageUrl?: string;
  imageLabel: string;
  notes: string;
  file?: File | null;
  hasImage: boolean;
}
interface Document {
  id: string;
  name: string;
  designer?: {
    user_id?: number;
    email?: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
  };
  feedback_user?: any;
  status?: string;
  isCurrent?: boolean;
}

const ProceedPhaseModal = ({
  isOpen,
  onClose,
  onRequestRevision,
  data,
  updateData,
  title,
  description,
  icon,
  iconStyle,
  iconContainerStyle,
  buttonText,
  projectId,
  revisions = [],
  onSuccess,
  currentStatus,
}: ProceedPhaseModalProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { triggerPhaseAnimation, triggerFailureAnimation, hidePhaseAnimation } =
    usePhaseAnimation();
  const animationStartRef = useRef<number | null>(null);
  const animationMinTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const steps = [
    { label: "Consultation", status: "consultation" },
    { label: "Preliminary", status: "preliminary" },
    { label: "Preliminary Finals", status: "preliminary_finals" },
    { label: "Final Review", status: "final_review" },
    { label: "Complete", status: "complete" },
  ];

  const getCurrentPhaseIndex = (status?: string): number => {
    if (!status) return 0;
    const statusLower = status.toLowerCase().replace(/[_\s-]/g, "");

    if (statusLower.includes("consultation")) return 0;
    if (statusLower.includes("preliminaryfinal")) return 2;
    if (statusLower.includes("preliminary")) return 1;
    if (statusLower.includes("finalreview") || statusLower.includes("final"))
      return 3;
    if (statusLower.includes("complete")) return 4;

    return 0;
  };

  const currentPhaseIndex = getCurrentPhaseIndex(currentStatus);
  const currentPhase = steps[currentPhaseIndex];
  const nextPhase =
    currentPhaseIndex < steps.length - 1
      ? steps[currentPhaseIndex + 1]
      : steps[steps.length - 1];

  const modalTitle = title || `Proceed to ${nextPhase.label}?`;
  const modalDescription = description || "Ready to move forward?";
  const actionButtonText = buttonText || `Proceed to ${nextPhase.label}`;

  const PHASE_COPY: Record<
    string,
    { paragraphs: string[]; checkboxLabel: string }
  > = {
    preliminary_finals: {
      paragraphs: [
        "By proceeding, you are approving and finalizing the floor plan and exterior elevations for the project.",
        "During this phase, our team will incorporate the electrical plan, plumbing rough-ins, cabinet elevations, and additional construction details. These elements must be developed after the floor plan and exterior have been finalized.",
        "Once the Preliminary Finals are complete, we will provide them for your review so any minor adjustments can be identified and addressed.",
      ],
      checkboxLabel:
        "I have reviewed all files and am ready to move forward to the next phase of the design process. I understand that by approving this stage, I am authorizing my designer to begin the Preliminary Finals. At this point, the floor plan and exterior design will be considered approved, and any future changes may result in additional design fees and delays to the project timeline.",
    },
    final_review: {
      paragraphs: [
        "By proceeding, you are approving your plans to move into the final review stage.",
        "During this phase, our design team will conduct a thorough final review of the plans to help ensure accuracy, consistency, and readiness for the building process.",
        "Once the final review is complete and all requirements have been met, your plans will be prepared for release and printing.",
        "If there are any minor changes in relation to the construction aspect of the plans, please request them now before proceeding.",
      ],
      checkboxLabel:
        "I have reviewed all files and am ready to move forward to the next phase of the design process. I understand that by approving this stage, I am authorizing my designer to complete the final review of the plans. At this point, the floor plan, exterior, and constructional aspects of the design will be considered approved, and any future changes may result in additional design fees and delays to the project timeline.",
    },
  };
  const phaseContent = PHASE_COPY[nextPhase.status] ?? null;
  const checkboxLabel =
    phaseContent?.checkboxLabel ??
    "I've reviewed all files and am ready to move forward to the next design phase. I understand this approval will notify my designer to proceed.";
  const cancelButtonText = `Request a Revision`;

  const clearAnimationMinTimer = () => {
    if (animationMinTimerRef.current) {
      clearTimeout(animationMinTimerRef.current);
      animationMinTimerRef.current = null;
    }
  };

  const hideAnimationWhenReady = () => {
    const start = animationStartRef.current;
    animationStartRef.current = null;
    if (start == null) return;
    const elapsed = Date.now() - start;
    if (elapsed >= ANIMATION_MAX_MS) {
      hidePhaseAnimation();
    } else {
      animationMinTimerRef.current = setTimeout(() => {
        animationMinTimerRef.current = null;
        hidePhaseAnimation();
      }, ANIMATION_MAX_MS - elapsed);
    }
  };

  const handleProceedToNextPhase = async () => {
    if (!isChecked) {
      toastMessage("error", "Please confirm that you've reviewed all files");
      return;
    }

    if (!projectId) {
      toastMessage("error", "Project ID is missing");
      return;
    }

    const isTestFailure =
      process.env.NODE_ENV === "development" &&
      typeof window !== "undefined" &&
      window.location.search.includes("testFailureAnimation=1");
    if (isTestFailure) {
      setIsLoading(true);
      setTimeout(() => {
        triggerFailureAnimation();
        handleErrors(new Error("Simulated failure for testing"));
        setIsLoading(false);
      }, 800);
      return;
    }

    animationStartRef.current = Date.now();
    triggerPhaseAnimation(nextPhase.label);

    try {
      setIsLoading(true);
      const response = await ProjectNextStatusService(projectId);

      clearAnimationMinTimer();
      hideAnimationWhenReady();

      if (response.status === 200) {
        toastMessage("success", "Project moved to next phase successfully");
        onClose();
        onSuccess?.({ nextPhaseLabel: nextPhase.label });
      } else {
        hidePhaseAnimation();
        triggerFailureAnimation();
        handleErrors(response);
      }
    } catch (error: any) {
      clearAnimationMinTimer();
      hidePhaseAnimation();
      triggerFailureAnimation();
      handleErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  const contractData: Document[] = revisions.map((rev, index) => ({
    id: rev.revision_id?.toString() || rev.id?.toString() || index.toString(),
    name: rev.title || `Revision ${rev.revision_id || index + 1}`,
    designer: rev.designer,
    feedback_user: rev.feedback_user,
    status: rev.status,
    isCurrent: index === 0,
  }));

  const getDisplayName = (doc: Document): string => {
    if (doc.feedback_user && doc.feedback_user !== null) {
      return doc.feedback_user.user_type || "Unknown User";
    }
    return "Designer";
  };

  const getUserName = (doc: Document): string => {
    if (doc.feedback_user && doc.feedback_user !== null) {
      return (
        doc.feedback_user.display_name ||
        `${doc.feedback_user.first_name || ""} ${doc.feedback_user.last_name || ""}`.trim() ||
        doc.feedback_user.email ||
        "Unknown User"
      );
    }
    return (
      doc.designer?.display_name ||
      `${doc.designer?.first_name || ""} ${doc.designer?.last_name || ""}`.trim() ||
      doc.designer?.email ||
      "Unknown Designer"
    );
  };

  const getStatusType = (doc: Document): string => {
    if (doc.feedback_user && doc.feedback_user !== null) {
      const userType = doc.feedback_user.user_type?.toLowerCase();
      if (userType === "client") {
        return "inprogress";
      } else if (userType === "collaborator") {
        return "pending";
      }
    }
    return "complete";
  };
  const [imageItems] = useState<ImageItem[]>([
    {
      id: "1",
      imageLabel: "Please enter the name",
      notes: "",
      hasImage: false,
    },
  ]);

  useEffect(() => {
    updateData?.({
      imageItems,
      imageLabel: imageItems[0]?.imageLabel || "",
      notes: imageItems[0]?.notes || "",
    });
  }, [imageItems, updateData]);

  return (
    <CustomModal
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      description={modalDescription}
      icon={icon}
      iconStyle={iconStyle}
      iconContainerStyle={iconContainerStyle}
      showBackButton={false}
      customContentContainer="noSidePadding"
      cancelButtonText={cancelButtonText}
      onCancel={onRequestRevision}
      cancelButtonContainerStyle={styles.customCancelButton}
      cancelButtonTitleStyle={styles.customCancelButtonTitle}
      actionButtonContainerStyle={styles.customActionButton}
      actionButtonTitleStyle={styles.customActionTitle}
      actionButtonText={actionButtonText}
      actionButtonIcon={Icons.FileAcceptIcon2}
      showModalHeader
      showModalFooter
      onNext={handleProceedToNextPhase}
      actionButtonLoading={isLoading}
      actionButtonDisabled={!isChecked || isLoading}
    >
      <div className={classNames(styles.modalContentContainer)}>
        <div className={classNames(styles.main, "flex flex-col gap-6")}>
          {phaseContent ? (
            <div className={classNames(styles.content, "flex flex-col gap-1")}>
              {phaseContent.paragraphs.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          ) : (
            <>
              <CustomPageHeading
                title=""
                description={`You're about to move from the ${currentPhase.label} Phase into ${nextPhase.label}.`}
                size="small"
              />
              <div className={classNames(styles.content, "flex flex-col gap-2")}>
                <div className="flex items-center gap-2">
                  <Icons.CheckMarkCircleIcon className={styles.icon} />
                  <p>
                    We’ll refine your plans based on the feedback you’ve already
                    provided
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Icons.CheckMarkCircleIcon className={styles.icon} />
                  <p>Most major layout decisions should now be close to final</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icons.CheckMarkCircleIcon className={styles.icon} />
                  <p>
                    You can still request changes, but they may be more limited
                    than before
                  </p>
                </div>
              </div>

              <CustomPageHeading
                title=""
                description="If you have any big-picture changes (overall layout, room locations, major design direction), please request them now before proceeding."
                size="small"
              />
            </>
          )}

          <CustomPageHeading title="Total Revisions" size="large" />

          <div className={styles.documentsList}>
            {contractData.length > 0 ? (
              contractData.map((doc) => (
                <div key={doc.id} className={styles.documentItem}>
                  <div className={styles.documentInfo}>
                    <Icons.FilePdfIcon className={styles.documentIcon} />
                    <span className={styles.documentName}>{doc.name}</span>
                  </div>

                  <div
                    className={classNames(
                      styles.customChanges,
                      "sm:flex-row flex flex-col items-center gap-2",
                    )}
                  >
                    {doc.isCurrent ? (
                      <CustomBadge
                        title={"Current Revision"}
                        statusType={"inprogress"}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <p>Changes Requested by:</p>

                        <span>
                          <CustomBadge
                            title={getDisplayName(doc)}
                            statusType={getStatusType(doc)}
                          />
                        </span>
                        <CustomPageHeading
                          title={getUserName(doc)}
                          size="xsmall"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.documentItem}>
                <CustomPageHeading
                  title=""
                  description="No revisions available in this phase"
                  size="medium"
                />
              </div>
            )}
          </div>

          <CustomCheckbox
            labelClass={styles.checkLabel}
            label={checkboxLabel}
            checked={isChecked}
            onChange={() => setIsChecked((prev) => !prev)}
            filtersCheckbox={styles.customCheckbox}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default ProceedPhaseModal;
